import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import InlineLoader from '@adsk/bim360-matrix-react-inline-loader';
import { useLocale } from '@adsk/bim360-matrix-react-helpers/es/locale';
import { MDText } from 'i18n-react';

import usePlugins from './helpers/hooks/usePlugins';
import { entityShape, pluginFactoryShape } from './helpers/propTypes';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon/es/SvgIcon';
import exclamation from '@adsk/bim360-matrix-react-assets/exclamation.svg';

import './_LinkedEntitiesCellView.scss';

const LinkedEntitiesCellView = ({ container, entities, plugins, isLoading, error }) => {
  const { typeToPlugin } = usePlugins(plugins);
  const { T } = useLocale(({ locale }) => new MDText(require(`./i18n/${locale}.json`)));
  const renderLinkedEntitiesTooltipContent = () => {
    if (isLoading || !entities.length) {
      return null;
    }
    return (
      <div>
        <div className="LinkedEntitiesCellView__tooltip-content-summary">
          {T.translate('ENTITY_LINKER_TITLE')}
        </div>
        <div className="LinkedEntitiesCellView__tooltip-content-main">
          {entities.map(entity => {
            const { entityType } = entity;
            const plugin = typeToPlugin[entityType];
            const { LinkedEntitiesCellBlock = () => null } = plugin || {};
            return <LinkedEntitiesCellBlock key={entity.id} entity={entity} />;
          })}
        </div>
      </div>
    );
  };

  const getEntityName = entity => {
    const plugin = typeToPlugin[entity.entityType];
    const { LinkedEntitiesCellItem } = plugin;
    return <LinkedEntitiesCellItem entity={entity} />;
  };

  const renderErrorText = () => {
    return (
      <div className="LinkedEntitiesCellView__error">
        <SvgIcon className="LinkedEntitiesCellView__icon" height="14px" width="14px">
          {exclamation}
        </SvgIcon>
        {T.translate('NOT_AVAILABLE')}
      </div>
    );
  };

  const renderText = () => {
    if (isLoading) {
      return (
        <div className="LinkedEntitiesCellView__loader">
          <InlineLoader />
        </div>
      );
    }
    if (error) {
      return renderErrorText();
    }

    if (!entities.length) {
      return '-';
    }
    return (
      <Fragment>
        <span className="LinkedEntitiesCellView__main-text">{getEntityName(entities[0])}</span>
        {entities.length > 1 ? (
          <span className="LinkedEntitiesCellView__secondary-text">+{entities.length - 1}</span>
        ) : null}
      </Fragment>
    );
  };

  return (
    <div className="LinkedEntitiesCellView">
      <Tooltip
        tooltipClassName="LinkedEntitiesCellView__tooltip-container"
        content={renderLinkedEntitiesTooltipContent()}
        place={Tooltip.Places.BOTTOM}
        triggerTarget={Tooltip.TriggerTargets.TOOLTIP}
        container={container}
        getTargetProps={() => {
          return { className: 'LinkedEntitiesCellView__tooltip-target' };
        }}
      >
        <div className="LinkedEntitiesCellView__text">{renderText()}</div>
      </Tooltip>
    </div>
  );
};

LinkedEntitiesCellView.propTypes = {
  /**
   * The target entity type plugins we want to allow linking to.
   */
  plugins: PropTypes.arrayOf(pluginFactoryShape).isRequired,
  /**
   * A container to attach the tooltip to
   */
  container: PropTypes.object,
  /**
   * arrays of links
   */
  entities: PropTypes.arrayOf(entityShape).isRequired,
  /**
   * is the data still loading
   */
  isLoading: PropTypes.bool,
  /**
   * is there an error on loading
   */
  error: PropTypes.bool
};

export default LinkedEntitiesCellView;
